import { el } from "../dom.ts";
import { type IconName, icon } from "./icon.ts";

/**
 * Buttons and link-buttons.
 *
 * A real `<button>` or `<a>` every time — never a clickable `div`. The platform
 * gives keyboard activation, focus order, form participation and the correct
 * accessibility role for free, and each of those is a defect waiting to happen
 * when re-implemented.
 *
 * One primary action per screen, per the UI UX Pro Max `primary-action` rule;
 * `secondary`, `ghost` and `danger` are visually subordinate to it.
 */

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger" | "brand-subtle";

export interface ButtonOptions {
  readonly label: string;
  readonly variant?: ButtonVariant;
  readonly size?: "md" | "sm";
  readonly block?: boolean;
  readonly type?: "button" | "submit";
  readonly icon?: IconName;
  readonly iconPosition?: "start" | "end";
  readonly disabled?: boolean;
  /**
   * Async in flight. Disables activation and exposes `aria-busy`, so the
   * double-submit that would otherwise create a duplicate request is not
   * reachable from the interface.
   */
  readonly loading?: boolean;
  readonly onClick?: (event: MouseEvent) => void;
}

function content(options: ButtonOptions): readonly (Node | string)[] {
  const glyph =
    options.icon === undefined
      ? undefined
      : el("span", { class: "nura-icon-slot", staticHtml: icon(options.icon, 18) });
  const label = el("span", { text: options.label });
  const spinner =
    options.loading === true
      ? el("span", { class: "nura-spinner", attrs: { "aria-hidden": true } })
      : undefined;

  if (spinner !== undefined) return [spinner, label];
  if (glyph === undefined) return [label];
  return options.iconPosition === "end" ? [label, glyph] : [glyph, label];
}

export function button(options: ButtonOptions): HTMLButtonElement {
  const busy = options.loading === true;
  const node = el(
    "button",
    {
      class: "nura-button",
      attrs: {
        type: options.type ?? "button",
        disabled: options.disabled === true || busy,
        "aria-busy": busy ? "true" : undefined,
      },
      data: {
        variant: options.variant ?? "secondary",
        size: options.size ?? "md",
        block: options.block === true ? "true" : undefined,
        loading: busy ? "true" : undefined,
      },
    },
    content(options),
  );

  if (options.onClick !== undefined) {
    node.addEventListener("click", options.onClick);
  }
  return node;
}

export interface LinkButtonOptions extends Omit<ButtonOptions, "type" | "loading"> {
  readonly href: string;
}

export function linkButton(options: LinkButtonOptions): HTMLAnchorElement {
  return el(
    "a",
    {
      class: "nura-button",
      attrs: {
        href: options.href,
        // A disabled link is not a thing the platform has; remove it from the
        // tab order and mark it instead of faking a disabled attribute.
        "aria-disabled": options.disabled === true ? "true" : undefined,
        tabindex: options.disabled === true ? "-1" : undefined,
      },
      data: {
        variant: options.variant ?? "secondary",
        size: options.size ?? "md",
        block: options.block === true ? "true" : undefined,
      },
    },
    content(options),
  );
}
